/**
 * Which lender a JOURNEY screen may show. Dependency-free so node:test can cover it
 * (`node --test src/lib/journeyLenderCore.test.js`); the React side is `useJourneyLender`.
 *
 * A journey screen must show the journey's own lender, never a guess. `useLender(key)` answers
 * a missing key with the primary lender — right for pre-journey screens, wrong on a journey
 * whose lender differs from the primary one (a pilot journey). Until the journey had loaded,
 * Work Details showed the primary lender's "powered by" logo, and a single failed read kept it
 * there; Rent Summary records consents that name the lender. So journey screens get null until
 * the journey itself has said which lender it is.
 */

/** Waits between re-asks while the journey has not answered: ~15 s in all, then give up. */
export const JOURNEY_LENDER_RETRY_DELAYS_MS = Object.freeze([1000, 2000, 4000, 8000]);

const cleanKey = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/** The lender a `journey/full` response names, or null for an error body or a missing field. */
export function lenderFromJourneyResponse(response) {
  return cleanKey(response?.journey?.lender);
}

/**
 * The lender key a journey screen may use right now.
 * - `known`: what the screen read from its own journey data. Always wins.
 * - `cached`: what an earlier screen learned, trusted only for the same session (`owner`), so a
 *   new sign-up in the same tab never inherits the previous tenant's lender.
 */
export function pickJourneyLenderKey({ known, cached, owner }) {
  const fromScreen = cleanKey(known);
  if (fromScreen) return fromScreen;
  if (!owner || !cached || cached.owner !== owner) return null;
  return cleanKey(cached.key);
}

/**
 * True when a profile gives `key` the name and short code that its logo and consent copy
 * need. Missing consent links do not count against it: that lender still renders as itself.
 */
export function isKnownLenderKey(liveProfiles, defaultProfiles, key) {
  const k = cleanKey(key);
  if (!k) return false;
  const hasIdentity = (profile) => Boolean(profile?.legalName && profile?.shortCode);
  return hasIdentity(liveProfiles?.[k]) || hasIdentity(defaultProfiles?.[k]);
}
